import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner, Image } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [partno, setPartno] = useState("");
  const [labourcharges, setlabourcharges] = useState("");
  const [hsnsacno, setHsnsacno] = useState("");
  const [igst, setIgst] = useState("");
  const [cgst, setCgst] = useState("");
  const [sgst, setSgst] = useState("");
  const [moc, setMoc] = useState("");
  const [brand, setBrand] = useState(null);
  const [isBrandSelected, setIsBrandSelected] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const [producttype, setProducttype] = useState('');
  const [unitofmeasurement, setUnitofmeasurement] = useState("");
  const [itemcode, setItemcode] = useState("");
  const [modelnumber, setModelnumber] = useState('');
  const [tagnumber, setTagnumber] = useState('');
  const [outsidedia, setOutsidedia] = useState('');
  const [wallthickness, setWallthickness] = useState('');
  const [innerdia, setInnerdia] = useState('');
  const [hardness, setHardness] = useState('');
  const [make, setMake] = useState('');
  const [size, setSize] = useState('');
  const [schedule, setSchedule] = useState('');
  const [classes, setClasses] = useState('');
  const [rating, setRating] = useState('');
  const [type, setType] = useState('');
  const [serialnumber, setSerialnumber] = useState('');
  const [productcategory, setProductcategory] = useState('');
  const [customername, setCustomername] = useState('');
  const [group, setGroup] = useState(null);
  const [groupOptions, setGroupOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // /PRODUCT IMAGES/
  const [fileInputs, setFileInputs] = useState([]);

  // Capitalization function - preserves existing capital letters
  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        // If word is already in uppercase, keep it as is
        if (word === word.toUpperCase() && word.length > 1) return word;
        // Otherwise capitalize first letter only, preserve rest of the word as typed
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Handle description input with capitalization
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    const capitalizedValue = capitalizeAllWords(value);
    setDescription(capitalizedValue);
  };

  // Fetch product data on component mount
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const [productRes, brandsRes, groupsRes, customersRes] = await Promise.all([
          axios.get(`${BASE_URL}product/findById/${id}`, { headers: authHeader() }),
          axios.get(BASE_URL + 'brand/findAllBrand', { headers: authHeader() }),
          axios.get(BASE_URL + 'productGroup/findAllProductGroup', { headers: authHeader() }),
          axios.get(BASE_URL + 'customer/findAllCustomer', { headers: authHeader() })
        ]);

        const productData = productRes.data;
        setBrandOptions(brandsRes.data.map(b => ({ value: b.id, label: b.brandname })));
        setGroupOptions(groupsRes.data.map(g => ({ value: g.id, label: g.groupname })));
        setCustomerOptions(customersRes.data.map(c => ({
          value: c.id,
          label: c.companyname + " - " + c.contactperson
        })));

        // Set product fields
        setDescription(productData.description || '');
        setPartno(productData.partno || '');
        setlabourcharges(productData.labourcharges || '')
        setHsnsacno(productData.hsnsacno || '');
        setIgst(productData.igst || '');
        setCgst(productData.cgst || '');
        setSgst(productData.sgst || '');
        setMoc(productData.moc || '');
        setProducttype(productData.producttype || '');
        setUnitofmeasurement(productData.unitofmeasurement || '');
        setItemcode(productData.itemcode || '');
        setModelnumber(productData.modelnumber || '');
        setTagnumber(productData.tagnumber || '');
        setOutsidedia(productData.outsidedia || '');
        setWallthickness(productData.wallthickness || '');
        setInnerdia(productData.innerdia || '');
        setHardness(productData.hardness || '');
        setMake(productData.make || '');
        setSize(productData.size || '');
        setSchedule(productData.schedule || '');
        setClasses(productData.classes || '');
        setRating(productData.rating || '');
        setType(productData.type || '');
        setSerialnumber(productData.serialnumber || '');
        setProductcategory(productData.productcategory || '');
        setCustomername(productData.customername || '');

        // Set relationship fields
        if (productData.brand) {
          setBrand({ value: productData.brand.id, label: productData.brand.brandname });
          setIsBrandSelected(true);
          setCategoryOptionsBasedOnBrand(productData.brand.brandname);
        }

        if (productData.customer) {
          setCustomer({
            value: productData.customer.id,
            label: productData.customer.companyname
          });
        }

        if (productData.productGroup) {
          setGroup({
            value: productData.productGroup.id,
            label: productData.productGroup.groupname
          });
        }

        setFileInputs(productData.productImages.map(image => ({
          id: image.id,
          file: null,
          filename: image.filename,
          isNew: false
        })));

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch product data. Please try again.',
        });
        navigate('/productindex/1');
      }
    };

    fetchProduct();
  }, [id, navigate]);

  // /PRODUCT IMAGES /

  const handleFileInputChange = (e, index) => {
    const { files } = e.target;
    const updatedFileInputs = [...fileInputs];
    updatedFileInputs[index] = { ...updatedFileInputs[index], file: files[0], filename: files[0] ? files[0].name : '' };
    setFileInputs(updatedFileInputs);
  };

  const handleAddFileInput = () => {
    setFileInputs([...fileInputs, { id: null, file: null, filename: '', isNew: true }]);
  };

  const handleRemoveFileInput = (index) => {
    const updatedFileInputs = [...fileInputs];
    updatedFileInputs.splice(index, 1);
    setFileInputs(updatedFileInputs);
  };

  const handleDeleteFile = async (id, index) => {
    try {
      await axios.delete(`${BASE_URL}productImages/delete/${id}`, {
        headers: authHeader(),
      });

      const updatedFileInputs = [...fileInputs];
      updatedFileInputs.splice(index, 1);
      setFileInputs(updatedFileInputs);

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete the file. Please try again.',
      });
    }
  };

  const setCategoryOptionsBasedOnBrand = (brandName) => {
    if (brandName === "Arai Spares") {
      setProductcategory("Pump Spare");
      setCategoryOptions([{ value: "Pump Spare", label: "Pump Spare" }]);
    } else if (brandName === "Manufacturing") {
      setCategoryOptions([
        { value: "International Manufacturing", label: "International Manufacturing" },
        { value: "Domestic Manufacturing", label: "Domestic Manufacturing" }
      ]);
    } else {
      setProductcategory('');
      setCategoryOptions([]);
    }
  };

  const handleGroupChange = async (selectedOption) => {
    setGroup(selectedOption);
  };

  const handleCustomerChange = (selectedOption) => {
    setCustomer(selectedOption);
  };

  const handleBrandChange = (selectedOption) => {
    setBrand(selectedOption);
    setIsBrandSelected(true);
    setCategoryOptionsBasedOnBrand(selectedOption.label);
  };

  const handleIgstChange = (e) => {
    const value = e.target.value;
    setIgst(value);
    const igstValue = parseFloat(value) || 0;
    const calculatedCGST = igstValue / 2;
    const calculatedSGST = igstValue / 2;
    setCgst(calculatedCGST.toString());
    setSgst(calculatedSGST.toString());
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const formData = new FormData();

    formData.append('product', JSON.stringify({
      id,
      productcategory,
      customer: customer ? { id: customer.value } : null,
      brand: brand ? { id: brand.value } : null,
      productGroup: group ? { id: group.value } : null,
      description,
      hsnsacno,
      igst,
      cgst,
      sgst,
      itemcode,
      producttype,
      unitofmeasurement,
      customername,
      partno,
      labourcharges,
      moc,
      modelnumber,
      serialnumber,
      tagnumber,
      outsidedia,
      wallthickness,
      innerdia,
      hardness,
      make,
      size,
      schedule,
      classes,
      rating,
      type,
    }));

    const files = fileInputs.filter(fileObj => fileObj.file);

    if (files.length > 0) {
      files.forEach(fileObj => {
        formData.append('[images]', fileObj.file);
      });
    } else {
      formData.append('[images]', new Blob([], { type: 'application/octet-stream' }));
    }

    try {
      const response = await axios.put(`${BASE_URL}product/update/${id}`, formData, {
        headers: authHeader(),
        'Content-Type': 'multipart/form-data'
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Product updated successfully!',
      }).then(() => {
        navigate('/productindex/1');
      });

    } catch (error) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = error.response.data;
        } else if (error.response.status === 401) {
          errorMessage = 'Either select the file or remove the choose file option.';
        } else {
          errorMessage = error.response.data.message || 'Something went wrong. Please try again.';
        }
      } else if (error.request) {
        errorMessage = 'No response from the server. Please check your network connection.';
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Common field groups
  const renderCommonFields = () => (
    <>
      <Col md="6">
        <Form.Group controlId="formbrand">
          <Form.Label>Brand</Form.Label>
          <Select
            value={brand}
            onChange={handleBrandChange}
            options={brandOptions}
            placeholder="Select Brand"
            isDisabled={isBrandSelected}
          />
        </Form.Group>
      </Col>

      <Col md="6">
        <Form.Group controlId="formproductcategory">
          <Form.Label>Product Category</Form.Label>
          <Form.Select
            disabled
            value={productcategory}
            onChange={(e) => setProductcategory(e.target.value)}
          >
            <option value="">Select Product Category</option>
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md="6">
        <Form.Group controlId="formgroup">
          <Form.Label>Stock Group</Form.Label>
          <Select
            value={group}
            onChange={handleGroupChange}
            options={groupOptions}
            placeholder="Select group"
          />
        </Form.Group>
      </Col>

      <Col md="6">
        <Form.Group controlId="formproducttype">
          <Form.Label>Product Type</Form.Label>
          <Form.Select
            disabled
            value={producttype}
            onChange={(e) => setProducttype(e.target.value)}
          >
            <option>Select Product Type</option>
            <option value="Raw Material">Raw Material</option>
            <option value="Semi Finished">Semi Finished</option>
            <option value="Finished">Finished</option>
            <option value="Services">Services</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );

  const renderPumpSpareFields = () => (
    <>
      <Col md="12">
        <Form.Group controlId="formdescription">
          <Form.Label>Product Description</Form.Label>
          <Form.Control
            type="text"
            value={description}
            onChange={handleDescriptionChange}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formbrand">
          <Form.Label>Customer Name</Form.Label>
          <Select
            value={customer}
            onChange={handleCustomerChange}
            options={customerOptions}
            placeholder="Select customer"
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formhsnsacno">
          <Form.Label>Model Number</Form.Label>
          <Form.Control
            type="text"
            value={modelnumber}
            onChange={(e) => setModelnumber(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formhsnsacno">
          <Form.Label>Tag Number</Form.Label>
          <Form.Control
            type="text"
            value={tagnumber}
            onChange={(e) => setTagnumber(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formdescription">
          <Form.Label>Item Code</Form.Label>
          <Form.Control
            type="text"
            value={itemcode}
            onChange={(e) => setItemcode(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formhsnsacno">
          <Form.Label>HSN Code</Form.Label>
          <Form.Control
            type="number"
            value={hsnsacno}
            onChange={(e) => setHsnsacno(e.target.value)}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formhsnsacno">
          <Form.Label>Serial Number</Form.Label>
          <Form.Control
            type="text"
            value={serialnumber}
            onChange={(e) => setSerialnumber(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formmoc">
          <Form.Label>MOC</Form.Label>
          <Form.Control
            type="text"
            value={moc}
            onChange={(e) => setMoc(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formpartno">
          <Form.Label>Part No</Form.Label>
          <Form.Control
            type="text"
            value={partno}
            onChange={(e) => setPartno(e.target.value.toUpperCase())}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formproducttype">
          <Form.Label>Unit Of Measurement</Form.Label>
          <Form.Select value={unitofmeasurement}
            onChange={(e) => setUnitofmeasurement(e.target.value)}
          >
            <option>Select Unit Of Measurement</option>
            <option value="bot">Bot</option>
            <option value="ctn">Ctn</option>
            <option value="days">Days</option>
            <option value="ft">Ft</option>
            <option value="hrs">Hrs</option>
            <option value="kg">Kg</option>
            <option value="grams">Grams</option>
            <option value="ltr">Ltr</option>
            <option value="mtr">Mtr</option>
            <option value="nos">Nos</option>
            <option value="pil">Pil</option>
            <option value="pkt">Pkt</option>
            <option value="set">Set</option>
            <option value="pcs">Pcs</option>
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formigst">
          <Form.Label>IGST</Form.Label>
          <Form.Control
            type="number"
            value={igst}
            onChange={handleIgstChange}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formcgst">
          <Form.Label>CGST</Form.Label>
          <Form.Control
            type="number"
            value={cgst}
            disabled
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formsgst">
          <Form.Label>SGST</Form.Label>
          <Form.Control
            type="number"
            value={sgst}
            disabled
          />
        </Form.Group>
      </Col>
    </>
  );

  const renderManufacturingFields = (isDomestic = false) => (
    <>
      <Col md="12">
        <Form.Group controlId="formdescription">
          <Form.Label>Product Description</Form.Label>
          <Form.Control
            type="text"
            value={description}
            onChange={handleDescriptionChange}
          />
        </Form.Group>
      </Col>

      {producttype === "Finished" && (
        <>
          <Col md="6">
            <Form.Group controlId="formbrand">
              <Form.Label>Customer Name</Form.Label>
              <Select
                value={customer}
                onChange={handleCustomerChange}
                options={customerOptions}
                placeholder="Select customer"
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Model Number</Form.Label>
              <Form.Control
                type="text"
                value={modelnumber}
                onChange={(e) => setModelnumber(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Tag Number</Form.Label>
              <Form.Control
                type="text"
                value={tagnumber}
                onChange={(e) => setTagnumber(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formdescription">
              <Form.Label>Item Code</Form.Label>
              <Form.Control
                type="text"
                value={itemcode}
                onChange={(e) => setItemcode(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>HSN Code</Form.Label>
              <Form.Control
                type="number"
                value={hsnsacno}
                onChange={(e) => setHsnsacno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control
                type="text"
                value={serialnumber}
                onChange={(e) => setSerialnumber(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formmoc">
              <Form.Label>MOC</Form.Label>
              <Form.Control
                type="text"
                value={moc}
                onChange={(e) => setMoc(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formpartno">
              <Form.Label>Part No</Form.Label>
              <Form.Control
                type="text"
                value={partno}
                onChange={(e) => setPartno(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formpartno">
              <Form.Label>labour Charges (%)</Form.Label>
              <Form.Control
                type="number"
                id="labourcharges"
                name="labourcharges"
                value={labourcharges || ''}
                onChange={(e) => setlabourcharges(e.target.value)}
                className="form-control"
                placeholder="Enter labour charges percentage"
              />
            </Form.Group>
          </Col>
        </>
      )}

      {(producttype === "Raw Material" || producttype === "Semi Finished") && (
        <>
          {producttype === "Semi Finished" && (
            <Col md="6">
              <Form.Group controlId="formdescription">
                <Form.Label>Item Code</Form.Label>
                <Form.Control
                  type="text"
                  value={itemcode}
                  onChange={(e) => setItemcode(e.target.value.toUpperCase())}
                />
              </Form.Group>
            </Col>
          )}
          <Col md="6">
            <Form.Group controlId="formmoc">
              <Form.Label>MOC</Form.Label>
              <Form.Control
                type="text"
                value={moc}
                onChange={(e) => setMoc(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Size</Form.Label>
              <Form.Control
                type="text"
                value={size}
                onChange={(e) => setSize(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formpartno">
              <Form.Label>Part No</Form.Label>
              <Form.Control
                type="text"
                value={partno}
                onChange={(e) => setPartno(e.target.value.toUpperCase())}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>HSN Code</Form.Label>
              <Form.Control
                type="number"
                value={hsnsacno}
                onChange={(e) => setHsnsacno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Schedule</Form.Label>
              <Form.Control
                type="text"
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="formhsnsacno">
              <Form.Label>Class</Form.Label>
              <Form.Control
                type="text"
                value={classes}
                onChange={(e) => setClasses(e.target.value)}
              />
            </Form.Group>
          </Col>

          {producttype === "Semi Finished" && (
            <>
              <Col md="6">
                <Form.Group controlId="formhsnsacno">
                  <Form.Label>Rating</Form.Label>
                  <Form.Control
                    type="text"
                    value={rating}
                    onChange={(e) => setRating(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formhsnsacno">
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </>
          )}
        </>
      )}

      <Col md="6">
        <Form.Group controlId="formproducttype">
          <Form.Label>Unit Of Measurement</Form.Label>
          <Form.Select value={unitofmeasurement}
            onChange={(e) => setUnitofmeasurement(e.target.value)}
          >
            <option>Select Unit Of Measurement</option>
            <option value="bot">Bot</option>
            <option value="ctn">Ctn</option>
            <option value="days">Days</option>
            <option value="ft">Ft</option>
            <option value="hrs">Hrs</option>
            <option value="kg">Kg</option>
            <option value="grams">Grams</option>
            <option value="ltr">Ltr</option>
            <option value="mtr">Mtr</option>
            <option value="nos">Nos</option>
            <option value="pil">Pil</option>
            <option value="pkt">Pkt</option>
            <option value="set">Set</option>
            <option value="pcs">Pcs</option>
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formigst">
          <Form.Label>IGST</Form.Label>
          <Form.Control
            type="number"
            value={igst}
            onChange={handleIgstChange}
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formcgst">
          <Form.Label>CGST</Form.Label>
          <Form.Control
            type="number"
            value={cgst}
            disabled
          />
        </Form.Group>
      </Col>
      <Col md="6">
        <Form.Group controlId="formsgst">
          <Form.Label>SGST</Form.Label>
          <Form.Control
            type="number"
            value={sgst}
            disabled
          />
        </Form.Group>
      </Col>
    </>
  );

  return (
    <div className="Container">
      <Card className="card">
        <Card.Header className="card-header">
          <h6>UPDATE PRODUCT</h6>
          <Link to="/productindex/1">
            <button className="button-70">BACK</button>
          </Link>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              {renderCommonFields()}

              {/* Pump Spare Fields */}
              {(productcategory === "Pump Spare") && renderPumpSpareFields()}

              {/* International Manufacturing Fields */}
              {(productcategory === "International Manufacturing") && renderManufacturingFields(false)}

              {/* Domestic Manufacturing Fields */}
              {(productcategory === "Domestic Manufacturing") && renderManufacturingFields(true)}

              <Col md="6">
                <Form.Group controlId="formfiles">
                  <Form.Label>Upload Files</Form.Label>
                  {fileInputs.map((fileObj, index) => (
                    <div key={index} className="mb-2">
                      <Form.Control
                        type="file"
                        onChange={(e) => handleFileInputChange(e, index)}
                      />
                      {fileObj.filename && (
                        <>
                          <p>{fileObj.filename}</p>
                          {!fileObj.isNew && fileObj.id && (
                            <button
                              type="button"
                              className="btn btn-link text-danger"
                              onClick={() => handleDeleteFile(fileObj.id, index)}
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                      {fileObj.isNew && index > 0 && (
                        <button
                          type="button"
                          className="btn btn-link text-danger"
                          onClick={() => handleRemoveFileInput(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary add-file"
                    onClick={handleAddFileInput}
                  >
                    Add File
                  </button>
                </Form.Group>
              </Col>

              <Col md="12" className="mt-3">
                <button
                  className="button-70"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : 'UPDATE'}
                </button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProductEdit;